import { useEffect, useState } from 'react';
import { Order } from '../../../../../../../clients/quipli-service/order/order';
import { useAPI } from '../../../../../../../clients/quipli-service/quipli-service-client';
import { PaymentMethodRequestV2 } from '../../../../../../../clients/quipli-service/payment/payment-v2';
import { Payment, PaymentMethodType } from '../../../../../../../clients/quipli-service/payment/payment';
import { toast } from '../../../../../../../util/toasts';
import { Invoice } from '../../../../../../../clients/quipli-service/invoice/invoice';
import { usePaymentApiV2 } from '../../../../../../../clients/quipli-service/payment/payment-api-v2';

export const useCollectPaymentModal = (orderId: number, onCancel: () => void, onComplete: () => void) => {
  const { getOrderById, getConfiguration } = useAPI();
  const { createPayment } = usePaymentApiV2();

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isPaying, setIsPaying] = useState<boolean>(false);
  const [order, setOrder] = useState<Order>();
  const [amount, setAmount] = useState<number>(0);
  const [facilitate, setFacilitate] = useState<boolean>(false);
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethodRequestV2>();
  const [invoice, setInvoice] = useState<Invoice>();
  const [payments, setPayments] = useState<Payment[]>([]);
  const [currency, setCurrency] = useState<string>('USD');
  const [balance, setBalance] = useState<number>(0);

  const doPayment = async () => {
    setIsPaying(true);
    try {
      if (invoice && paymentMethod && amount && amount > 0) {
        const payment = await createPayment({
          invoiceIds: [invoice.id],
          paymentMethod,
          amount: amount,
          userId: invoice.customer.id,
        });
        if (!payment) {
          toast('Payment failed', 'error');
          return;
        }

        if (paymentMethod.type === PaymentMethodType.Stripe && paymentMethod.terminalId) {
          setFacilitate(true);
          setPayments([payment]);
          return;
        } else {
          if (payment.gatewayStatus === 'requires_action') {
            toast('Payment requires authentication', 'error');
          } else if (payment.gatewayStatus == 'processing') {
            toast('Bank payment is processing, once approved you may proceed with on rent');
          } else if (payment.status === 'failed') {
            toast('Payment failed', 'error');
            return;
          }
          toast('Payment successful', 'success');
          await onComplete();
        }
      }
    } catch (error) {
      toast('Failed to create payment', 'error');
    } finally {
      setIsPaying(false);
    }
  };

  const postPayment = async (payments?: Payment[]) => {
    setFacilitate(false);
    if (payments) {
      if (payments[0]?.gatewayStatus === 'requires_action') {
        toast('Payment requires authentication', 'error');
      } else if (payments[0]?.gatewayStatus == 'processing') {
        toast('Bank payment is processing, once approved you may proceed with on rent');
        return;
      } else if (payments[0]?.status === 'failed') {
        toast('Payment failed', 'error');
        return;
      }
    }
    await doPayment();
    await onComplete();
  };

  const init = async () => {
    const order = await getOrderById(orderId);
    setInvoice(order.invoices.find((inv) => inv));
    setOrder(order);
    const confs = await getConfiguration('CONF_CURRENCY');
    const currency = confs?.value;
    setCurrency(currency || 'USD');
    const amount = order.invoices.find((inv) => inv.balance > 0)?.balance ?? 0;
    setBalance(amount);
    setAmount(amount);
  };

  useEffect(() => {
    let isMounted = true;
    (async () => {
      try {
        await init();
      } catch (error) {
        toast('Failed to initialize payment', 'error');
      } finally {
        if (isMounted) setIsLoading(false);
      }
    })();
    return () => {
      isMounted = false;
    };
  }, [orderId]);

  return {
    postPayment,
    amount,
    setAmount: (a: number) => setAmount(Number(a.toFixed(2))),
    order,
    paymentMethod,
    setPaymentMethod,
    facilitate,
    setFacilitate,
    isLoading,
    isPaying,
    onClose: onCancel,
    payments,
    setPayments,
    currency,
    balance,
  };
};
